import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Grid,
  Box,
  Button,
  Card,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  useDeleteLocationMutation,
  useGetallEnumSearchQuery,
  useGetallLocationQuery,
} from "../../../Api/attoDeskApi";
import {
  IEnumType,
  IStudentLocation,
} from "../../../Api/Interface/api.interface";
import NewPopUpDepartment from "../Create/location";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import Pagination from "../../../Components/Pagination";
import useIsMobile from "../../../Core/hook/useIsMobile";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import { useTheme } from "@mui/material/styles";
import NewPopUpEnum from "../Create/enum";
import AddIcon from "@mui/icons-material/Add";

enum ManageType {
  country = "country",
  province = "province",
  city = "city",
}

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const isMobileRes = useIsMobile();
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [selectedCoursing, setSelectedCoursing] =
    useState<IStudentLocation | null>(null);

  const [selectedenum, setSelectedEnum] = useState<IEnumType | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openCreateDialogEnum, setOpenCreateDialogEnum] = useState(false);
  const [openEditDialogEnum, setOpenEditDialogEnum] = useState(false);

  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] =
    useState<IStudentLocation | null>(null);

  const [selectedType, setSelectedType] = useState<ManageType>(
    ManageType.country
  );

  const { data, isLoading, isError, refetch } = useGetallLocationQuery({
    searchText: currentSearchQuery,
  });

  const { data: enumData, isLoading: enumIsLoading } = useGetallEnumSearchQuery(
    {
      searchText: currentSearchQuery,
      enum_type: selectedType,
    }
  );

  // Reference lists used to resolve a location's country/province/city names.
  const { data: countryRef } = useGetallEnumSearchQuery({
    searchText: "",
    enum_type: ManageType.country,
  });
  const { data: provinceRef } = useGetallEnumSearchQuery({
    searchText: "",
    enum_type: ManageType.province,
  });
  const { data: cityRef } = useGetallEnumSearchQuery({
    searchText: "",
    enum_type: ManageType.city,
  });

  const nameFromRef = (
    ref: typeof countryRef,
    id?: number | null
  ): string | undefined => {
    if (id == null) return undefined;
    const list = Array.isArray(ref?.data) ? (ref?.data as IEnumType[]) : [];
    return list.find((x) => Number(x.id) === Number(id))?.enum_value;
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDialog = (coursing: IStudentLocation) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenCraeteDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleOpenCraeteDialogEnum = () => {
    setOpenCreateDialogEnum(true);
  };

  const handleCloseCreateDialogEnum = () => {
    setOpenCreateDialogEnum(false);
  };

  const handleOpenEditDialogEnum = (enumType: IEnumType) => {
    setSelectedEnum(enumType);
    setOpenEditDialogEnum(true);
  };

  const handleCloseEditDialogEnum = () => {
    setOpenEditDialogEnum(false);
    setSelectedEnum(null);
  };

  const handleOpenDeletePopup = (coursing: IStudentLocation) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const menuItems = [
    { label: "Country", value: ManageType.country },
    { label: "Province", value: ManageType.province },
    { label: "City", value: ManageType.city },
  ];

  const [deleteCategory] = useDeleteLocationMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the location");
    }
  };

  const coursings: IStudentLocation[] = Array.isArray(data?.data)
    ? (data?.data as IStudentLocation[])
    : [];

  const enumDataList: IEnumType[] = Array.isArray(enumData?.data)
    ? (enumData?.data as IEnumType[])
    : [];

  const sectionCardSx = {
    backgroundColor: theme.palette.cardAdd.bgColor,
    border: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderRadius: "16px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    overflow: "hidden",
  } as const;

  const sectionHeaderSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 1.5,
    px: 3,
    py: 2,
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  } as const;

  const paginated = coursings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* ============================ SECTION 1: LOCATIONS ============================ */}
      <Card sx={sectionCardSx}>
        <Box sx={sectionHeaderSx}>
          <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
            Locations
          </Typography>
          <Button
            className={classes.addNewButton}
            startIcon={<AddIcon />}
            onClick={handleOpenCraeteDialog}
          >
            Add Location
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="160px"
            >
              <StepUpSpinner size="medium" />
            </Box>
          ) : isError || coursings.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 6,
              }}
            >
              <LocationOnOutlinedIcon
                sx={{ fontSize: 44, color: theme.palette.cardAdd.borderColor }}
              />
              <Typography sx={{ color: "text.secondary" }}>
                {isError ? "Error fetching data" : "No locations found."}
              </Typography>
              {!isError && (
                <Button
                  className={classes.addNewButton}
                  startIcon={<AddIcon />}
                  onClick={handleOpenCraeteDialog}
                >
                  Add New
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {paginated.map((row: IStudentLocation) => {
                  const region = [
                    nameFromRef(cityRef, row.city_id),
                    nameFromRef(provinceRef, row.province_id),
                    nameFromRef(countryRef, row.country_id),
                  ]
                    .filter(Boolean)
                    .join(", ");

                  return (
                    <Grid item lg={4} md={6} sm={6} xs={12} key={row.id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: theme.palette.cardAdd.bgColor,
                          border: `1px solid ${theme.palette.cardAdd.borderColor}`,
                          borderRadius: "12px",
                          boxShadow: "none",
                          p: 2.5,
                          transition:
                            "transform 0.15s ease, box-shadow 0.15s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        {/* Title row with colour indicator */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            mb: 1.5,
                          }}
                        >
                          <Tooltip
                            title={row.location_color || ""}
                            arrow
                            disableHoverListener={!row.location_color}
                          >
                            <Box
                              sx={{
                                flexShrink: 0,
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                backgroundColor:
                                  row.location_color ||
                                  theme.palette.primary.main,
                                boxShadow: `0 0 0 2px ${theme.palette.cardAdd.bgColor}, 0 0 0 3px ${theme.palette.cardAdd.borderColor}`,
                              }}
                            />
                          </Tooltip>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.location_name ?? ""}
                          </Typography>
                        </Box>

                        {/* Details */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            flexGrow: 1,
                          }}
                        >
                          {region && (
                            <Typography
                              variant="body2"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "text.secondary",
                              }}
                            >
                              <LocationOnOutlinedIcon
                                fontSize="small"
                                sx={{ color: "#f47856" }}
                              />
                              {region}
                            </Typography>
                          )}
                          {row.location_address && (
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", pl: "28px" }}
                            >
                              {row.location_address}
                            </Typography>
                          )}
                          {row.contact_number && (
                            <Typography
                              variant="body2"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "text.secondary",
                              }}
                            >
                              <CallOutlinedIcon
                                fontSize="small"
                                sx={{ color: "#f47856" }}
                              />
                              {row.contact_number}
                            </Typography>
                          )}
                        </Box>

                        {/* Actions */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            mt: 2,
                          }}
                        >
                          <Button
                            className={classes.editButtonList}
                            onClick={() => handleOpenDialog(row)}
                          >
                            <EditSharpIcon className={classes.editListIcon} />
                          </Button>
                          <Button
                            className={classes.deleteButtonList}
                            onClick={() => handleOpenDeletePopup(row)}
                          >
                            <DeleteOutlineSharpIcon
                              className={classes.editListIcon}
                            />
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {coursings.length > rowsPerPage && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Pagination
                    count={coursings.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        {openCreateDialog && (
          <NewPopUpDepartment
            openModel={openCreateDialog}
            handleCloseDialog={handleCloseCreateDialog}
          />
        )}
        {selectedCoursing && (
          <NewPopUpDepartment
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(coursingToDelete.id.toString());
            }}
            title="Delete Location"
            content={`Are you sure you want to delete "${coursingToDelete.location_name}"?`}
          />
        )}
      </Card>

      {/* ============================ SECTION 2: MANAGE ============================ */}
      <Card sx={sectionCardSx}>
        <Box sx={sectionHeaderSx}>
          <Box
            sx={{
              display: "inline-flex",
              gap: 0.5,
              p: 0.5,
              borderRadius: "10px",
              backgroundColor: theme.palette.action.hover,
            }}
          >
            {menuItems.map((item) => {
              const isActive = selectedType === item.value;
              return (
                <Button
                  key={item.value}
                  disableElevation
                  onClick={() => setSelectedType(item.value)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    minWidth: 92,
                    px: 2,
                    py: 0.5,
                    borderRadius: "8px",
                    boxShadow: "none",
                    color: isActive ? "#ffffff" : theme.palette.primary.main,
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isActive
                        ? theme.palette.primary.main
                        : theme.palette.action.selected,
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
          <Button
            className={classes.addNewButton}
            startIcon={<AddIcon />}
            onClick={handleOpenCraeteDialogEnum}
          >
            Add New
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {enumIsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="120px"
            >
              <StepUpSpinner size="medium" />
            </Box>
          ) : enumDataList.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                py: 4,
              }}
            >
              <Typography sx={{ color: "text.secondary" }}>
                No {selectedType} records found.
              </Typography>
              <Button
                className={classes.addNewButton}
                startIcon={<AddIcon />}
                onClick={handleOpenCraeteDialogEnum}
              >
                Add New
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {enumDataList.map((item: IEnumType) => (
                <Chip
                  key={item.id}
                  label={item.enum_value || "Undefined"}
                  onClick={() => handleOpenEditDialogEnum(item)}
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 0.5,
                    backgroundColor: theme.palette.primary.main,
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Card>

      {openCreateDialogEnum && (
        <NewPopUpEnum
          openModel={openCreateDialogEnum}
          handleCloseDialog={handleCloseCreateDialogEnum}
          enum_type={selectedType}
        />
      )}
      {openEditDialogEnum && selectedenum && (
        <NewPopUpEnum
          openModel={openEditDialogEnum}
          handleCloseDialog={handleCloseEditDialogEnum}
          data={selectedenum}
        />
      )}
    </Box>
  );
};

export default Index;
